import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import CellDate from 'src/registry/components/table/cells/CellDate';
import useSorting from 'src/registry/hooks/useSorting';
import { generalMessages } from 'src/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useListSubscriptionItemPricingOverrides } from 'src/features/subscriptionItemPricingOverrides/api/useListSubscriptionItemPricingOverrides';
import CellSubscriptionItemPricingOverrideActions from 'src/features/subscriptionItemPricingOverrides/components/tables/CellSubscriptionItemPricingOverrideActions';
import CreateSubscriptionItemPricingOverrideFormModal from 'src/features/subscriptionItemPricingOverrides/components/forms/CreateSubscriptionItemPricingOverrideFormModal';
import { PRICING_OVERRIDE_STATUS_BADGE_OPTIONS } from 'src/features/subscriptionItemPricingOverrides/utils/constants';
import { pricingOverridesMessages } from 'src/features/subscriptionItemPricingOverrides/utils/messages';
import { SubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/utils/types';
import { formatCoefficient } from 'src/features/subscriptionItemPricingOverrides/utils/formatCoefficient';

const SubscriptionItemPricingOverridesTable = () => {
    const { formatMessage } = useIntl();
    const { subscriptionId = '', subscriptionItemId = '' } = useParams();

    const permissionManageSubscriptions = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: false }],
    });

    const {
        data: pricingOverrides,
        isLoading,
        isError,
    } = useListSubscriptionItemPricingOverrides({
        subscriptionId,
        itemId: subscriptionItemId,
        ...querySortingOptions,
        config: { enabled: !!subscriptionId && !!subscriptionItemId },
    });

    const columnHelper = createColumnHelper<SubscriptionItemPricingOverride>();

    const columns = [
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: { title: formatMessage(servicePlansMessages.version) },
            maxSize: 45,
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: PRICING_OVERRIDE_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('coefficient', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: ({ row }) => `×${formatCoefficient(row.original.coefficient)}`,
            meta: { title: formatMessage(pricingOverridesMessages.coefficient) },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            cell: CellDate,
            enableSorting: true,
            meta: { title: formatMessage(generalMessages.validFrom) },
        }),
        columnHelper.accessor('valid_to', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: CellDate,
            meta: { title: formatMessage(generalMessages.validTo) },
        }),
        ...(permissionManageSubscriptions
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellSubscriptionItemPricingOverrideActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<SubscriptionItemPricingOverride, unknown>
            columns={columns}
            data={pricingOverrides}
            semantics={{
                title: formatMessage(pricingOverridesMessages.pricingOverrides),
                entryName: formatMessage(pricingOverridesMessages.pricingOverride),
                entryNames: formatMessage(pricingOverridesMessages.pricingOverrides).toLowerCase(),
                placeholder: formatMessage(pricingOverridesMessages.pricingOverride),
            }}
            sorting={sorting}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageSubscriptions && { newEntry: CreateSubscriptionItemPricingOverrideFormModal }),
            }}
        />
    );
};

export default SubscriptionItemPricingOverridesTable;
